import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";

import Container from "@mui/material/Container";
import PanelLoadingIndicator from "../components/PanelLoadingIndicator";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch } from "react-redux";
import { logOut } from "../auth/authActions";
import UserTable from "./UserTable";
import { useSearchFilter } from "../common/use-searchFilter";
import SearchBar from "../creator-application/common/SearchBar";
import OrganizationAutoComplete from "./OrganizationAutoComplete";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Switch } from "@mui/material";
import Grid from "@mui/material/Grid";
import CreateUserDialog from "./CreateUserDialog";
import { useNavigate, useLocation, useResolvedPath } from "react-router-dom";
import { clone } from "ramda";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import { notBlank } from "../util/ext";

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    root: {
      display: "flex",
      flexDirection: "column",
      height: "95%",
    },

    panel: {
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: "white",
      backgroundColor: "white",
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },

    toolbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      height: 60,
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: "white",
      backgroundColor: "white",
      color: theme.palette.primary.main,
      padding: theme.spacing(4),
    },

    leftContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      minWidth: 200,
    },

    rightContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      flexGrow: 1,
    },

    title: {
      flexGrow: 1,
      display: "flex",
      alignItems: "center",
      fontSize: "2.0rem",
      fontWeight: "bold",
    },

    count: {
      marginRight: 10,
      fontSize: "2.0rem",
      fontWeight: "bold",
    },

    newButton: {
      textTransform: "uppercase",
      color: "#127DA9",
      fontSize: "1.2rem",
      fontWeight: "bold",
    },

    listContainer: {
      height: "calc(100vh - 275px)",
      overflow: "auto",
    },

    filters: {
      // minWidth: 650,
      // width: "auto",
      paddingTop: 3,
      marginLeft: theme.spacing(2),
    },
  }));

const UserBrowseView = ({
  searchState,
  onSearchStateChange,
  searchResults,
}) => {
  const path = useResolvedPath(".").pathname;
  const navigate = useNavigate();
  const location = useLocation();
  const [scrollVisible, setScrollVisible] = useState(true);
  const classes = useStyles(scrollVisible)();
  const reduxDispatch = useDispatch();

  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);

  //-- Handle Search state
  const initialState = clone(searchState);
  const {
    state,
    handleUpdateSort,
    handleUpdateFilter,
    handleUpdatePage,
    handleUpdateOrganization,
    handleUpdateActiveOnly,
  } = useSearchFilter(initialState);

  //-- Whenever the internal search state changes, update the search state stored in the parent
  useEffect(() => {
    onSearchStateChange(state);
    setScrollVisible(true);
  }, [onSearchStateChange, state]);

  useEffect(() => {
    if (searchResults.isError && searchResults.error.status === 401) {
      reduxDispatch(logOut());
    }

    if (searchResults.isSuccess) {
      setScrollVisible(true);
    }
  }, [
    searchResults.data,
    searchResults.isError,
    searchResults.error,
    reduxDispatch,
    searchResults.isSuccess,
  ]);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  return (
    <Container
      id="user-browse-view"
      maxWidth={false}
      classes={{ root: classes.root }}
    >
      <CreateUserDialog
        open={createUserDialogOpen}
        onCancel={useCallback(() => setCreateUserDialogOpen(false), [])}
        onSave={userId => navigate(`${path}/${userId}`)}
      />

      <div>
        <SearchBar
          query={state.appliedFilter}
          onQueryChange={handleUpdateFilter}
          backButton={
            notBlank(location.state) &&
            notBlank(location.state.previousLocation) && (
              <Button
                aria-label="Go back"
                onClick={() => navigate(location.state.previousLocation)}
                startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              >
                Back
              </Button>
            )
          }
          otherFilters={
            <Grid
              container
              alignItems="center"
              direction="row"
              spacing={2}
              classes={{ root: classes.filters }}
            >
              <Grid item xs={5} md={6}>
                <OrganizationAutoComplete
                  value={state.appliedOrganization}
                  label="Filter By Organization"
                  onChange={handleUpdateOrganization}
                />
              </Grid>

              <Grid item>
                <FormControlLabel
                  control={
                    <Switch
                      checked={state.activeOnly}
                      onChange={event =>
                        handleUpdateActiveOnly(event.target.checked)
                      }
                      name="active-only"
                      color="primary"
                    />
                  }
                  label="Show Active Only"
                />
              </Grid>
            </Grid>
          }
        />

        <div>
          <div className={classes.toolbar}>
            <div className={classes.leftContainer}>
              <div className={classes.title}>
                <div className={classes.count}>
                  {searchResults.isSuccess
                    ? searchResults.data.count.toLocaleString()
                    : 0}
                </div>
                <div>Users</div>
              </div>
            </div>

            <div>
              <div>
                <Button
                  variant={"text"}
                  startIcon={<AddIcon />}
                  classes={{ root: classes.newButton }}
                  onClick={useCallback(() => setCreateUserDialogOpen(true), [])}
                >
                  New User
                </Button>
              </div>
            </div>
          </div>
        </div>

        {searchResults.isLoading && <PanelLoadingIndicator />}
        {searchResults.isSuccess && (
          <Container maxWidth={false} classes={{ root: classes.panel }}>
            <UserTable
              userList={searchResults.data.contents}
              total={searchResults.data.count}
              page={state.page}
              orderBy={state.orderBy}
              order={state.orderDirection}
              onSortChange={handleUpdateSort}
              onPageChange={handleUpdatePage}
              onScrollVisible={handleScrollVisible}
            />
          </Container>
        )}
        {searchResults.isError && <div>Error</div>}
      </div>
    </Container>
  );
};

export default UserBrowseView;
